import React, { useCallback, useMemo, useState } from "react";

import { Grid, MenuItem, SelectChangeEvent, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import classnames from "classnames";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { BsTrash } from "react-icons/bs";

import { useAppState } from "../../../../context/StateContext";
import { AgentServiceBooking } from "../../../../models/agent.service.booking";
import { Currency } from "../../../../models/currency";
import { EstimateGroup, EstimateService } from "../../../../models/estimate";
import { Measurement } from "../../../../models/measurement";
import {
  Service as ServiceBooking,
  ChargeOptions,
  ChargeOptionsEnum,
} from "../../../../models/service";
import ServiceBookingService from "../../../../services/service.booking.service";
import { Status } from "../../../../util/enums";
import { getMeasurement } from "../../../../util/getMeasurement";
import { TotalItemValues } from "../../../../util/inventory";
import {
  calculateSubtotalByChargeAndByEstimate,
  checkValueMoreThan0,
  formatCurrency,
  getEstimateServiceArrayDefault,
  getMarginAndMarkupByRate,
  getPriceStringValid,
  getRateByChargeSelected,
} from "../../../../util/prices";
import { Button, Input, Select } from "../../../Form";
import AutoSuggest from "../../../Form/AutoSuggest";
import { Color } from "../../../Form/Button";
import { NotificationToast } from "../../Toast/NotificationToast";
import { AllowedColumn, EstimateServiceColumns, IconColor, ServiceColumn, ValueOf } from "../types";
import useStyles from "./styles";

type ColumnServiceFocused = [column: string, row: number];

interface ServiceSectionProps {
  estimateServices?: EstimateService[];
  groupIndex: number;
  ["data-cy"]?: string;
  allowedColumns: AllowedColumn[];
  group?: EstimateGroup;
  isEstimateMode?: boolean;
  totalInventoryValues: TotalItemValues;
  allCurrencies: Currency[];
  agentServices?: AgentServiceBooking[];
  onChangeEstimateServices: (groupIndex: number, estimateServices: EstimateService[]) => void;
  isOnViewMode?: boolean;
  onCustomerView?: boolean;
}

export const ServiceSection: React.FC<ServiceSectionProps> = (props) => {
  const {
    estimateServices,
    allowedColumns,
    group,
    isEstimateMode,
    totalInventoryValues,
    allCurrencies,
    onChangeEstimateServices,
    groupIndex,
    agentServices = [],
    isOnViewMode = false,
    onCustomerView = false,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const { currentUser } = useAppState();

  const [allServices, setAllServices] = useState<ServiceBooking[]>([]);

  const measurement = getMeasurement();

  useQuery(
    ["service.list"],
    () =>
      ServiceBookingService.getAll({
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["sellDescription", "asc"]]),
        status: Status.ACTIVE,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array)
          setAllServices(response.data);
      },
    }
  );

  const getDataCy = useCallback(
    (key: string) => `${props["data-cy"]}${props["data-cy"] ? "-" : ""}${key}`,
    [props["data-cy"]]
  );

  const checkIfColumnIsAllowed = useCallback(
    (columnName: keyof EstimateServiceColumns) => {
      if (!allowedColumns || allowedColumns.length <= 0 || !columnName) return false;

      return allowedColumns.map((allowedColumn) => allowedColumn.column).includes(columnName);
    },
    [allowedColumns]
  );

  const serviceColumns: ServiceColumn[] = useMemo(() => {
    return allowedColumns.map((allowedColumn) => {
      return {
        key: allowedColumn.column,
        name: `${t(allowedColumn.column)}`,
        style: allowedColumn.style || {},
        className: allowedColumn.className || "",
      } as ServiceColumn;
    });
  }, [allowedColumns]);

  const getDimenstionFeeOption = useCallback((measurement?: Measurement) => {
    const fee = t("DimensionFee").replace("{dimension}", measurement?.code || "");
    const split = fee.split(" ");

    return `${split[0]?.toUpperCase()} ${split[1]}`;
  }, []);

  const chargeOptions: { key: keyof ChargeOptions; label: string }[] = useMemo(() => {
    return [
      {
        key: ChargeOptionsEnum.Fee,
        label: getDimenstionFeeOption(measurement),
      },
      { key: ChargeOptionsEnum.Flat, label: t("flatFee") },
      { key: ChargeOptionsEnum.Hourly, label: t("hourlyFee") },
      { key: ChargeOptionsEnum["Half Hour"], label: t("halfHourFee") },
    ];
  }, [measurement]);

  const hasOneService = useMemo(() => (estimateServices?.length || 0) <= 1, [estimateServices]);

  const getCurrencyById = (currencyId?: number) =>
    allCurrencies.find((currency) => Number(currency.id) === Number(currencyId));

  const getRateByChargeAgentServiceOrDefault = (
    charge: keyof ChargeOptions,
    agentService?: AgentServiceBooking,
    defaultValue?: string | number
  ) => {
    if (!charge) return defaultValue;
    switch (charge) {
      case ChargeOptionsEnum.Fee:
        return checkValueMoreThan0(agentService?.m3Fee, defaultValue);
      case ChargeOptionsEnum.Flat:
        return checkValueMoreThan0(agentService?.flatFee, defaultValue);
      case ChargeOptionsEnum.Hourly:
        return checkValueMoreThan0(agentService?.hourlyFee, defaultValue);
      case ChargeOptionsEnum["Half Hour"]:
        return checkValueMoreThan0(agentService?.halfHourFee, defaultValue);
      default:
        return defaultValue;
    }
  };

  const [focused, setFocused] = useState<ColumnServiceFocused>(["", -1]);
  const [rate, setRate] = useState("");
  const [markup, setMarkup] = useState("");
  const [margin, setMargin] = useState("");
  const [discount, setDiscount] = useState("");

  const renderTextByKey = (column: ServiceColumn, data: EstimateService, groupName?: string) => {
    const { key, style, className } = column;
    const currency = getCurrencyById(data.currencyId);

    switch (key) {
      case "service":
        return (
          <Grid
            data-cy={getDataCy(`group-${groupName}-service-rows-${key}`)}
            item
            key={key}
            style={style}
            className={classnames(classes.serviceRow, className)}
          >
            <span className={classes.columnValue}>{data?.name}</span>
          </Grid>
        );
      case "charge":
        return (
          <Grid
            data-cy={getDataCy(`group-${groupName}-service-rows-${key}`)}
            item
            key={key}
            style={style}
            className={classnames(classes.serviceRow, className)}
          >
            <span className={classes.columnValue}>{data[key]}</span>
          </Grid>
        );
      case "quantity":
        return (
          <Grid
            data-cy={getDataCy(`group-${groupName}-service-rows-${key}`)}
            item
            key={key}
            style={style}
            className={classnames(classes.serviceRow, className)}
          >
            <span className={classes.columnValue}>
              {data.charge === ChargeOptionsEnum.Fee && isEstimateMode
                ? totalInventoryValues.volumeFormatted
                : data[key]}
            </span>
          </Grid>
        );
      case "currency":
        return (
          <Grid
            data-cy={getDataCy(`group-${groupName}-service-rows-${key}`)}
            item
            key={key}
            style={style}
            className={classnames(classes.serviceRow, className)}
          >
            <span className={classes.columnValue}>{currency?.name}</span>
          </Grid>
        );
      case "subTotal":
        // eslint-disable-next-line no-case-declarations
        const subTotal = calculateSubtotalByChargeAndByEstimate(
          data.charge as keyof ChargeOptions,
          data.rate,
          data.quantity,
          data.discount,
          data.margin,
          data.markUp,
          totalInventoryValues.volumeConverted
        );

        return (
          <Grid
            data-cy={getDataCy(`group-${groupName}-service-rows-${key}`)}
            item
            key={key}
            style={style}
            className={classnames(classes.serviceRow, className)}
            container
            alignItems="center"
          >
            <span className={classes.subtotal}>{formatCurrency(subTotal, data.currency)}</span>
          </Grid>
        );

      default:
        return null;
    }
  };

  const renderServiceInputByKey = (
    column: ServiceColumn,
    data: EstimateService,
    estimateServiceIndex: number,
    groupName?: string
  ) => {
    const { key, style, className } = column;
    const currency = getCurrencyById(data.currencyId);
    const decimalCase = Number(currency?.decimalPoints || 2);

    const onChangeInput = (value: ValueOf<EstimateService>) => {
      if (value !== null && value !== undefined)
        onChangeEstimateService([key as keyof EstimateService], [value], estimateServiceIndex);
    };

    const onChangeChargeAndRate = (charge: keyof ChargeOptions) => {
      const agentService = agentServices.find((aService) => aService.serviceId === data?.serviceId);
      const rate = getRateByChargeAgentServiceOrDefault(
        charge,
        agentService,
        getRateByChargeSelected(charge, data, data?.rate)
      );

      onChangeEstimateService(["charge", "rate"], [charge, rate], estimateServiceIndex);
    };

    const onChangeMarkupAndMargin = (
      markup?: string | number,
      margin?: string | number,
      newRate?: string | number
    ) => {
      const rate = Number(newRate || data?.rate || 0) || 0;
      const { margin: newMargin, markup: newMarkup } = getMarginAndMarkupByRate(
        rate,
        markup,
        margin
      );

      onChangeEstimateService(["margin", "markUp"], [newMargin, newMarkup], estimateServiceIndex);
    };

    const onChangeRateAndMargin = () => {
      let replaced = getPriceStringValid(rate, currency?.decimalSeparator);
      const nValue = Number(replaced);

      if (Number.isNaN(nValue)) replaced = "0";

      const { margin: newMargin } = getMarginAndMarkupByRate(nValue, data?.markUp, undefined);

      onChangeEstimateService(["rate", "margin"], [replaced, newMargin], estimateServiceIndex);
    };

    const formatCurrencyToInput = (value?: string | number) => {
      if (!value || Number.isNaN(Number(value))) return formatCurrency(0, currency, false);

      return formatCurrency(Number(value).toFixed(decimalCase), currency, false);
    };
    const formatNumberToInput = (value?: string | number) => {
      if (!value || Number.isNaN(Number(value))) return Number(0).toFixed(decimalCase);

      return Number(value).toFixed(decimalCase);
    };

    const isFocused = focused[0] === key && focused[1] === estimateServiceIndex;

    const notHasRate = !data.rate || Number.isNaN(data.rate) || Number(data.rate) <= 0;

    switch (key) {
      case "service":
        return (
          <Grid
            data-cy={getDataCy(`group-${groupName}-service-rows-${key}`)}
            item
            key={key}
            style={style}
            className={classnames(classes.serviceRow, className)}
          >
            <AutoSuggest
              disabled={isOnViewMode}
              placeholder={t("searchService")}
              inputValue={data?.name || ""}
              getOptionLabel={(service?: ServiceBooking) => String(service?.sellDescription)}
              getOptionSelected={(option?: ServiceBooking, value?: ServiceBooking) =>
                option?.id === value?.id
              }
              options={allServices}
              onChange={(service?: ServiceBooking) => {
                const agentService = agentServices.find(
                  (aService) => aService.serviceId === service?.id
                );
                const rateDefault = getRateByChargeSelected(
                  (service?.defaultFee || data.charge) as keyof ChargeOptions,
                  service,
                  data.rate
                );
                const rate = getRateByChargeAgentServiceOrDefault(
                  (service?.defaultFee || data.charge) as keyof ChargeOptions,
                  agentService,
                  rateDefault
                );

                onChangeEstimateService(
                  ["serviceId", "service", "name", "charge", "rate", "margin"],
                  [
                    service?.id || null,
                    service || null,
                    service?.sellDescription || null,
                    service?.defaultFee || data.charge,
                    rate,
                    service?.margin || data?.margin,
                  ],
                  estimateServiceIndex
                );
              }}
              onChangeInput={(value, reason) => {
                if (reason === "input") {
                  onChangeEstimateService(["name"], [value || null], estimateServiceIndex);
                }
              }}
            />
          </Grid>
        );
      case "charge":
        return (
          <Grid
            data-cy={getDataCy(`group-${groupName}-service-rows-${key}`)}
            item
            key={key}
            style={style}
            className={classnames(classes.serviceRow, className)}
          >
            <Select
              disabled={isOnViewMode}
              value={data[key] || ""}
              onChange={(e: SelectChangeEvent<any>) => onChangeChargeAndRate(e.target.value)}
              name={key}
              placeholder={t("selectAnOption")}
              displayEmpty
            >
              {chargeOptions.map((charge) => (
                <MenuItem key={charge.key} value={charge.key}>
                  {charge.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        );
      case "rate":
        return (
          <Grid
            data-cy={getDataCy(`group-${groupName}-service-rows-${key}`)}
            item
            key={key}
            style={style}
            className={classnames(classes.serviceRow, className)}
          >
            <Input
              disabled={isOnViewMode}
              name={key}
              value={isFocused ? rate : formatCurrencyToInput(data[key])}
              onFocus={() => {
                setRate(String(formatCurrencyToInput(data[key])));
                setFocused([key, estimateServiceIndex]);
              }}
              onChange={(e) => {
                setRate(e.target.value);
              }}
              onBlur={() => {
                onChangeRateAndMargin();
                setRate("");
                setFocused(["", -1]);
              }}
              placeholder={"0.00"}
              leftIcon={currency?.symbol || "$"}
              inputTextAlign={"start"}
            />
          </Grid>
        );
      case "quantity":
        return (
          <Grid
            data-cy={getDataCy(`group-${groupName}-service-rows-${key}`)}
            item
            key={key}
            style={style}
            className={classnames(classes.serviceRow, className)}
          >
            <Input
              name={key}
              value={
                data.charge === ChargeOptionsEnum.Fee && isEstimateMode
                  ? totalInventoryValues.volumeFormatted
                  : data[key]
              }
              disabled={
                isOnViewMode ||
                notHasRate ||
                (data.charge === ChargeOptionsEnum.Fee && isEstimateMode)
              }
              onChange={(e) => {
                const onlyNumber = e.target.value.replace(/\D/g, "");
                const withPointer = getPriceStringValid(e.target.value, ".");
                const replaced =
                  data.charge === ChargeOptionsEnum["Half Hour"] ? withPointer : onlyNumber;

                onChangeInput(replaced);
              }}
              placeholder={"0"}
              inputTextAlign={"center"}
            />
          </Grid>
        );
      case "markUp":
        return (
          <Grid
            data-cy={getDataCy(`group-${groupName}-service-rows-${key}`)}
            item
            key={key}
            style={style}
            className={classnames(classes.serviceRow, className)}
          >
            <Input
              name={key}
              disabled={isOnViewMode || notHasRate}
              value={isFocused ? markup : formatCurrencyToInput(data[key])}
              onFocus={() => {
                setMarkup(String(formatCurrencyToInput(data[key])));
                setFocused([key, estimateServiceIndex]);
              }}
              onChange={(e) => {
                setMarkup(e.target.value);
              }}
              onBlur={() => {
                const replaced = getPriceStringValid(markup, currency?.decimalSeparator);
                const nValue = Number(replaced);

                if (Number.isNaN(nValue)) {
                  onChangeMarkupAndMargin(0, undefined);

                  return;
                }
                onChangeMarkupAndMargin(replaced, undefined);
                setMarkup("");
                setFocused(["", -1]);
              }}
              placeholder={"0.00"}
              leftIcon={currency?.symbol || "$"}
              inputTextAlign={"start"}
            />
          </Grid>
        );
      case "margin":
        return (
          <Grid
            data-cy={getDataCy(`group-${groupName}-service-rows-${key}`)}
            item
            key={key}
            style={style}
            className={classnames(classes.serviceRow, className)}
          >
            <Input
              name={key}
              disabled={isOnViewMode || notHasRate}
              value={isFocused ? margin : formatNumberToInput(data[key])}
              onFocus={() => {
                setMargin(formatNumberToInput(data[key]));
                setFocused([key, estimateServiceIndex]);
              }}
              onChange={(e) => {
                setMargin(e.target.value);
              }}
              onBlur={() => {
                const replaced = getPriceStringValid(margin, currency?.decimalSeparator);
                const nValue = Number(replaced);

                if (Number.isNaN(nValue)) {
                  onChangeMarkupAndMargin(undefined, 0);

                  return;
                }
                onChangeMarkupAndMargin(undefined, replaced);
                setMargin("");
                setFocused(["", -1]);
              }}
              placeholder={"0"}
              rightIcon={"%"}
              inputTextAlign={"center"}
            />
          </Grid>
        );
      case "discount":
        return (
          <Grid
            data-cy={getDataCy(`group-${groupName}-service-rows-${key}`)}
            item
            key={key}
            style={style}
            className={classnames(classes.serviceRow, className)}
          >
            <Input
              name={key}
              disabled={isOnViewMode || notHasRate}
              value={isFocused ? discount : formatNumberToInput(data[key])}
              onFocus={() => {
                setDiscount(formatNumberToInput(data[key]));
                setFocused([key, estimateServiceIndex]);
              }}
              onChange={(e) => {
                setDiscount(e.target.value);
              }}
              onBlur={() => {
                const replaced = getPriceStringValid(discount, currency?.decimalSeparator);
                const nValue = Number(replaced);

                if (Number.isNaN(nValue)) {
                  onChangeInput("0");

                  return;
                }
                if (nValue > 100) {
                  onChangeInput("100");

                  return;
                }
                onChangeInput(replaced);
                setDiscount("");
                setFocused(["", -1]);
              }}
              placeholder={"0"}
              rightIcon={"%"}
              inputTextAlign={"center"}
            />
          </Grid>
        );
      case "currency":
        // eslint-disable-next-line no-case-declarations
        const getOptionLabel = (localCurrency?: Currency) =>
          `${String(localCurrency?.code || "")} ${localCurrency?.code ? "-" : -""} ${String(
            localCurrency?.name || ""
          )}`;

        return (
          <Grid
            data-cy={getDataCy(`group-${groupName}-service-rows-${key}`)}
            item
            key={key}
            style={style}
            className={classnames(classes.serviceRow, className)}
          >
            <AutoSuggest
              disabled={isOnViewMode}
              value={`${currency ? currency?.code : ""}`}
              placeholder={t("searchCurrency")}
              inputValue={`${currency ? currency?.code : ""}`}
              getOptionLabel={getOptionLabel}
              getOptionSelected={(option?: Currency, value?: Currency) => option?.id === value?.id}
              options={allCurrencies}
              fillInputValueOnChange={(value) => {
                return !(value === getOptionLabel(currency));
              }}
              onChange={(suggestCurrency: Currency) => {
                onChangeEstimateService(
                  ["currencyId", "currency"],
                  [suggestCurrency?.id || null, suggestCurrency || null],
                  estimateServiceIndex
                );
              }}
            />
          </Grid>
        );
      case "subTotal":
        // eslint-disable-next-line no-case-declarations
        const subTotal = calculateSubtotalByChargeAndByEstimate(
          data.charge as keyof ChargeOptions,
          data.rate,
          data.quantity,
          data.discount,
          data.margin,
          data.markUp,
          totalInventoryValues.volumeConverted
        );

        return (
          <Grid
            data-cy={getDataCy(`group-${groupName}-service-rows-${key}`)}
            item
            key={key}
            style={style}
            className={classnames(classes.serviceRow, className)}
            container
            alignItems="center"
          >
            <span className={classes.subtotal}>{formatCurrency(subTotal, data.currency)}</span>
          </Grid>
        );
      case "actions":
        return isOnViewMode ? null : (
          <Grid
            item
            key={key}
            data-cy={getDataCy(`group-${groupName}-service-rows-${key}`)}
            style={style}
            className={classnames(classes.serviceRow, className)}
            container
            direction="row"
            alignItems="center"
          >
            <Tooltip
              title={t(
                `${hasOneService ? "thereMustBeAtLeastOneServiceInAGroup" : "deleteThisLine"}`
              )}
              placement="top"
            >
              <div data-cy={`group-${groupName}-service-rows-delete`}>
                <BsTrash
                  onClick={() => {
                    if (!hasOneService) onDeleteEstimateService(estimateServiceIndex);
                  }}
                  color={hasOneService ? IconColor.DISABLED : IconColor.DANGER}
                  className={!hasOneService ? classes.button : ""}
                  size={18}
                />
              </div>
            </Tooltip>
          </Grid>
        );
      default:
        return null;
    }
  };

  const onAddServiceLineToGroup = () => {
    const currency = currentUser?.company?.currency;
    const cloneServices = _.cloneDeep(estimateServices || []) as EstimateService[];

    cloneServices.push(getEstimateServiceArrayDefault(currentUser?.companyId, currency)[0]);
    onChangeEstimateServices(groupIndex, cloneServices);
    NotificationToast.success(t("serviceAddedSuccessfully"), { id: "new-group-success" });
  };

  const onChangeEstimateService = (
    keys: Array<keyof EstimateService>,
    values: Array<any>,
    estimateServiceIndex: number
  ) => {
    const cloneServices = _.cloneDeep(estimateServices || []) as EstimateService[];
    const estimateService = cloneServices[estimateServiceIndex];

    if (estimateService) {
      keys.forEach((key, keyIndex) => {
        estimateService[key] = values[keyIndex];
      });
    }
    onChangeEstimateServices(groupIndex, cloneServices);
  };

  const onDeleteEstimateService = (estimateServiceIndex: number) => {
    const cloneServices = _.cloneDeep(estimateServices || []) as EstimateService[];

    if (cloneServices && cloneServices[estimateServiceIndex]) {
      cloneServices.splice(estimateServiceIndex, 1);
      onChangeEstimateServices(groupIndex, cloneServices);
    }
  };

  return (
    <Grid
      data-cy={getDataCy(`group-${group?.name}-service`)}
      container
      direction="column"
      className={classes.groupContent}
    >
      <Grid
        data-cy={getDataCy(`group-${group?.name}-service-columns`)}
        container
        direction="row"
        className={classes.serviceColumns}
      >
        {serviceColumns.map((column) => {
          if (checkIfColumnIsAllowed(column.key)) {
            if (column?.key === "actions" && isOnViewMode) return null;

            return (
              <Grid
                key={column.key}
                data-cy={getDataCy(`group-${group?.name}-service-column-${column.name}`)}
                item
                className={classnames(classes.serviceColumn, column.className)}
                style={column.style}
              >
                <span
                  className={
                    !["actions", "subTotal"].includes(column.key) ? classes.iconMargin : ""
                  }
                >
                  {column.name}
                </span>
              </Grid>
            );
          }

          return null;
        })}
      </Grid>
      <Grid container direction="row" data-cy={getDataCy(`group-${group?.name}-service-rows`)}>
        {!onCustomerView
          ? group?.estimateServices?.map((service, estimateServiceIndex) =>
              serviceColumns.map(
                (column) =>
                  checkIfColumnIsAllowed(column.key) &&
                  renderServiceInputByKey(column, service, estimateServiceIndex, group?.name)
              )
            )
          : group?.estimateServices?.map((service) =>
              serviceColumns.map(
                (column) =>
                  checkIfColumnIsAllowed(column.key) &&
                  renderTextByKey(column, service, group?.name)
              )
            )}
      </Grid>

      {!isOnViewMode && (
        <Grid
          data-cy={getDataCy(`group-${group?.name}-service-new`)}
          container
          justifyContent="flex-end"
          alignItems="center"
          className={classes.newLineContent}
        >
          <Button
            customColor={Color.INFO}
            onClick={() => onAddServiceLineToGroup()}
            data-cy={getDataCy(`group-${group?.name}-service-new-button`)}
          >
            <span
              data-cy={getDataCy(`group-${group?.name}-service-new-button-label`)}
              className={classes.buttonLabel}
            >
              {t("addNewLine")}
            </span>
          </Button>
        </Grid>
      )}
    </Grid>
  );
};
